import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import { ProductType } from "@b2bportal/core-types";
import { activitiesApi } from "@b2bportal/core-utilities";
import {
  ActivitiesCancelScenarioEnum,
  type ActivitiesCancelScenario,
  type PollCancelFulfillResponse,
  type PollCancelFulfillRequest,
  type PollCancelQuoteRequest,
  PollCancelQuoteResponseEnum,
  type PollCancelQuoteResponseSuccess,
  type ScheduleCancelFulfillRequest,
  type ScheduleCancelQuoteRequest,
  type ScheduleCancelQuoteResponse,
  type ScheduleCancelFulfillResponse,
  type ActivityReservation,
} from "@b2bportal/rbc-activities-api";
import { trackEvent } from "@hopper-b2b/api";
import { SelfServeEvents } from "@hopper-b2b/types";
import {
  ACTIVITIES_CANCEL_FULFILL_REASON_CODE,
  SELF_SERVE_CANCEL_POLL_INTERVAL_MS,
} from "@/utilities";

const QUOTE_POLL_MAX_ITERATIONS = 90;
const FULFILL_POLL_MAX_ITERATIONS = 90;

const clearPollTimerId = (ref: MutableRefObject<number | undefined>) => {
  if (ref.current != null) {
    window.clearInterval(ref.current);
    ref.current = undefined;
  }
};

export enum ActivityCancelStep {
  LoadingOrProcessing = "LoadingOrProcessing",
  Error = "Error",
  Fulfilling = "Fulfilling",
  Complete = "Complete",
  PolicyInfo = "PolicyInfo",
  ContactSupport = "ContactSupport",
  BookingPending = "BookingPending",
  CancellationPending = "CancellationPending",
  TerminalInfo = "TerminalInfo",
}

const parseFulfillPollData = (
  data: PollCancelFulfillResponse
): PollCancelQuoteResponseEnum => {
  if (data?.PollCancelFulfillResponse) {
    return data.PollCancelFulfillResponse;
  }
  return PollCancelQuoteResponseEnum.Pending;
};

const mapScenarioToStep = (
  scenario: ActivitiesCancelScenario
): ActivityCancelStep => {
  switch (scenario.ActivitiesCancelScenario) {
    case ActivitiesCancelScenarioEnum.FullyRefundable:
    case ActivitiesCancelScenarioEnum.PartiallyRefundable:
    case ActivitiesCancelScenarioEnum.NonRefundable:
      return ActivityCancelStep.PolicyInfo;
    case ActivitiesCancelScenarioEnum.ContactCustomerService:
      return ActivityCancelStep.ContactSupport;
    case ActivitiesCancelScenarioEnum.BookingPending:
      return ActivityCancelStep.BookingPending;
    case ActivitiesCancelScenarioEnum.CancellationPending:
      return ActivityCancelStep.CancellationPending;
    case ActivitiesCancelScenarioEnum.AlreadyCanceled:
    case ActivitiesCancelScenarioEnum.NonCancellable:
    case ActivitiesCancelScenarioEnum.PastActivityStart:
      return ActivityCancelStep.TerminalInfo;
    default:
      return ActivityCancelStep.Error;
  }
};

const buildScheduleRequest = (
  reservation: ActivityReservation
): ScheduleCancelQuoteRequest | null => {
  const orderedProductId = reservation.bookingSession?.cartOrderedProductId;
  if (!orderedProductId) return null;
  return {
    customerReservationId: reservation.customerReservationId,
    orderedProductId,
  };
};

type LegacyPascalCaseCancelQuoteIds = {
  QuoteId?: string;
  SessionId?: string;
};

type CancelQuoteScheduleResponseBody =
  | ScheduleCancelQuoteResponse
  | ScheduleCancelFulfillResponse
  | (Partial<ScheduleCancelQuoteResponse | ScheduleCancelFulfillResponse> &
      LegacyPascalCaseCancelQuoteIds & {
        value?: CancelQuoteScheduleResponseBody;
        data?: CancelQuoteScheduleResponseBody;
      });

const toPollCancelQuotePayload = (
  scheduleBody: CancelQuoteScheduleResponseBody
): PollCancelQuoteRequest | PollCancelFulfillRequest | null => {
  let raw: CancelQuoteScheduleResponseBody | null =
    scheduleBody != null && typeof scheduleBody === "object"
      ? scheduleBody
      : null;
  if (!raw) return null;

  const pollQuoteData = (raw as any).value ?? (raw as any).data;
  if (
    pollQuoteData != null &&
    typeof pollQuoteData === "object" &&
    !Array.isArray(pollQuoteData)
  ) {
    raw = pollQuoteData;
  }

  const quoteId = (raw as any).quoteId ?? (raw as any).QuoteId;
  const sessionId = (raw as any).sessionId ?? (raw as any).SessionId;
  if (typeof quoteId !== "string" || typeof sessionId !== "string") {
    return null;
  }
  if (quoteId.length === 0 || sessionId.length === 0) {
    return null;
  }
  return { quoteId, sessionId };
};

export interface UseActivityCancelPolicyProps {
  reservation: ActivityReservation;
  onFulfilled?: () => void;
}

export const useActivityCancelPolicy = ({
  reservation,
  onFulfilled,
}: UseActivityCancelPolicyProps) => {
  const [step, setStep] = useState<ActivityCancelStep>(
    ActivityCancelStep.LoadingOrProcessing
  );
  const [scenario, setScenario] = useState<ActivitiesCancelScenario | null>(
    null
  );
  const pollIdRef = useRef<number>();
  /** Success quote id from {@link PollCancelQuoteResponseSuccess} (fulfillment quote, not cart quote). */
  const fulfillQuoteIdRef = useRef<string | null>(null);
  const isMountedRef = useRef<boolean>(true);

  const scheduleRequest = useMemo(
    () => buildScheduleRequest(reservation),
    [reservation]
  );

  const clearPollInterval = useCallback(() => clearPollTimerId(pollIdRef), []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      clearPollTimerId(pollIdRef);
    };
  }, []);

  const pollCancelQuote = useCallback(
    async (cancelQuotePollPayload: PollCancelQuoteRequest) => {
      let iterations = 0;
      let settled = false;
      await new Promise<void>((resolve, reject) => {
        const runQuotePollTick = async () => {
          if (settled) return;
          iterations += 1;
          // FE handles quote timeout - reject after max iterations to show error state
          if (iterations > QUOTE_POLL_MAX_ITERATIONS) {
            settled = true;
            clearPollInterval();
            reject(new Error("cancel quote poll timeout"));
            return;
          }
          try {
            const res =
              await activitiesApi.apiV0SelfserveActivitiesCancelQuotePollPut(
                cancelQuotePollPayload
              );
            if (settled) return;
            const data = res.data;
            if (
              data.PollCancelQuoteResponse ===
              PollCancelQuoteResponseEnum.Pending
            ) {
              return;
            }
            if (
              data.PollCancelQuoteResponse ===
              PollCancelQuoteResponseEnum.Failure
            ) {
              settled = true;
              clearPollInterval();
              reject(new Error("cancel quote failure"));
              return;
            }
            if (
              data.PollCancelQuoteResponse ===
              PollCancelQuoteResponseEnum.Success
            ) {
              settled = true;
              clearPollInterval();
              const success = data as PollCancelQuoteResponseSuccess;
              fulfillQuoteIdRef.current = success.quoteId;
              if (isMountedRef.current) {
                setScenario(success.cancelScenario);
                setStep(mapScenarioToStep(success.cancelScenario));
              }
              resolve();
            }
          } catch (e) {
            if (settled) return;
            settled = true;
            clearPollInterval();
            reject(e);
          }
        };

        pollIdRef.current = window.setInterval(() => {
          void runQuotePollTick();
        }, SELF_SERVE_CANCEL_POLL_INTERVAL_MS);
        void runQuotePollTick();
      });
    },
    [clearPollInterval]
  );

  const getPolicyInfo = useCallback(async () => {
    clearPollInterval();
    fulfillQuoteIdRef.current = null;
    if (isMountedRef.current) {
      setScenario(null);
    }
    if (!scheduleRequest) {
      if (isMountedRef.current) {
        setStep(ActivityCancelStep.Error);
      }
      return;
    }
    if (isMountedRef.current) {
      setStep(ActivityCancelStep.LoadingOrProcessing);
    }
    trackEvent({
      eventName: SelfServeEvents.ClickCancel,
      properties: { product: ProductType.ACTIVITIES },
    });
    try {
      // scheduleRequest is validated by buildScheduleRequest to match ScheduleCancelQuoteRequest
      const scheduleRes =
        await activitiesApi.apiV0SelfserveActivitiesCancelQuoteSchedulePost(
          scheduleRequest
        );
      const pollPayload = toPollCancelQuotePayload(scheduleRes.data);
      if (!pollPayload) {
        if (isMountedRef.current) {
          setStep(ActivityCancelStep.Error);
        }
        return;
      }
      await pollCancelQuote(pollPayload);
    } catch (error) {
      console.error("Error getting cancellation policy info:", error);
      if (isMountedRef.current) {
        setStep(ActivityCancelStep.Error);
      }
    }
  }, [scheduleRequest, clearPollInterval, pollCancelQuote]);

  const confirmCancellation = useCallback(async () => {
    if (!scheduleRequest) return;
    const fulfillQuoteId = fulfillQuoteIdRef.current;
    if (!fulfillQuoteId) {
      if (isMountedRef.current) {
        setStep(ActivityCancelStep.Error);
      }
      return;
    }

    const scheduleFulfillBody: ScheduleCancelFulfillRequest = {
      quoteId: fulfillQuoteId,
      cancelReasonCode: ACTIVITIES_CANCEL_FULFILL_REASON_CODE,
    };

    // Validate fulfillment body has required fields
    if (!scheduleFulfillBody.quoteId || !scheduleFulfillBody.cancelReasonCode) {
      if (isMountedRef.current) {
        setStep(ActivityCancelStep.Error);
      }
      return;
    }

    clearPollInterval();
    if (isMountedRef.current) {
      setStep(ActivityCancelStep.Fulfilling);
    }
    trackEvent({
      eventName: SelfServeEvents.ConfirmCancelation,
      properties: { product: ProductType.ACTIVITIES },
    });
    try {
      const fulfillScheduleRes =
        await activitiesApi.apiV0SelfserveActivitiesCancelFulfillSchedulePost(
          scheduleFulfillBody
        );

      const fulfillPollPayload = toPollCancelQuotePayload(
        fulfillScheduleRes.data
      );
      if (!fulfillPollPayload) {
        if (isMountedRef.current) {
          setStep(ActivityCancelStep.Error);
        }
        return;
      }

      // Validate poll payload has required fields
      if (!fulfillPollPayload.quoteId || !fulfillPollPayload.sessionId) {
        if (isMountedRef.current) {
          setStep(ActivityCancelStep.Error);
        }
        return;
      }

      let iterations = 0;
      let settled = false;
      await new Promise<void>((resolve, reject) => {
        const runFulfillPollTick = async () => {
          if (settled) return;
          iterations += 1;
          // FE handles fulfill timeout - reject after max iterations to show error state
          if (iterations > FULFILL_POLL_MAX_ITERATIONS) {
            settled = true;
            clearPollInterval();
            reject(new Error("cancel fulfill poll timeout"));
            return;
          }
          try {
            const res =
              await activitiesApi.apiV0SelfserveActivitiesCancelFulfillPollPut(
                fulfillPollPayload
              );
            if (settled) return;
            const outcome = parseFulfillPollData(res.data);
            if (outcome === PollCancelQuoteResponseEnum.Success) {
              settled = true;
              clearPollInterval();
              resolve();
            } else if (outcome === PollCancelQuoteResponseEnum.Failure) {
              settled = true;
              clearPollInterval();
              reject(new Error("cancel fulfill failure"));
            }
          } catch (e) {
            if (settled) return;
            settled = true;
            clearPollInterval();
            reject(e);
          }
        };

        pollIdRef.current = window.setInterval(() => {
          void runFulfillPollTick();
        }, SELF_SERVE_CANCEL_POLL_INTERVAL_MS);
        void runFulfillPollTick();
      });
      if (isMountedRef.current) {
        setStep(ActivityCancelStep.Complete);
        onFulfilled?.();
      }
    } catch (error) {
      console.error("Error confirming cancellation:", error);
      if (isMountedRef.current) {
        setStep(ActivityCancelStep.Error);
      }
    }
  }, [scheduleRequest, clearPollInterval, onFulfilled]);

  return {
    step,
    scenario,
    getPolicyInfo,
    confirmCancellation,
  };
};
